import { cn, generateTextShadow, REWARD_ICONS } from "@/lib/utils";
import { PrizeWrapper } from "@/components/PrizeWrapper";
import { AvatarBorder } from "@/components/AvatarBorder";
import type { CSSProperties } from "react";

// Shared sparkle burst configuration
const SPARKLE_BURSTS = [
  { x: -180, y: -80, size: 36, delay: 0.05, shape: "diamond" },
  { x: -100, y: -180, size: 24, delay: 0.1, shape: "dot" },
  { x: 20, y: -220, size: 30, delay: 0.12, color: "#B8FFB0", shape: "dot" },
  { x: 120, y: -160, size: 24, delay: 0.08, shape: "diamond" },
  { x: 190, y: -40, size: 24, delay: 0.14, shape: "dot" },
  { x: 140, y: 100, size: 36, delay: 0.18, shape: "diamond" },
  { x: 0, y: 180, size: 30, delay: 0.16, shape: "dot" },
  { x: -140, y: 120, size: 24, delay: 0.2, shape: "dot" },
  { x: -220, y: -10, size: 18, delay: 0.06, shape: "diamond" },
  { x: -40, y: -200, size: 16, delay: 0.09, shape: "dot" },
  { x: 60, y: -200, size: 14, delay: 0.11, shape: "diamond" },
  { x: 220, y: 10, size: 16, delay: 0.13, shape: "dot" },
  { x: 60, y: 170, size: 18, delay: 0.17, shape: "dot" },
  { x: -200, y: 40, size: 14, delay: 0.19, shape: "diamond" },
] as const;

interface RevealOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  rewardText: string;
  rewardValue: number;
  buttonText: string;
  description?: string;
  shouldUseRewardIcons?: boolean;
  alternatePrizeImageSrc?: string;
  alternatePrizeImageSrcSize?: string;
  alternatePrizeText?: string;
  isCosmetic?: boolean;
  userProfilePicture?: string;
}

export function RevealOverlay({
  isOpen,
  onClose,
  title,
  rewardText,
  rewardValue,
  buttonText,
  description,
  shouldUseRewardIcons = true,
  alternatePrizeImageSrc,
  alternatePrizeImageSrcSize,
  alternatePrizeText,
  isCosmetic = false,
  userProfilePicture,
}: RevealOverlayProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-1002! flex items-center justify-center bg-black/92 px-6 text-left">
      <style>{`
        @keyframes gacha-pop {
          0% {
            transform: scale(0.2) rotate(-12deg);
            opacity: 0;
          }
          60% {
            transform: scale(1.18) rotate(6deg);
            opacity: 1;
          }
          80% {
            transform: scale(0.95) rotate(-2deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }

        @keyframes gacha-float-in {
          0% {
            transform: translateY(12px) scale(0.98);
            opacity: 0;
          }
          100% {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes gacha-bob {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }

        @keyframes gacha-sparkle {
          0% {
            transform: translate(-50%, -50%) scale(0.2) rotate(0deg);
            opacity: 0;
          }
          35% {
            opacity: 1;
          }
          100% {
            transform: translate(
                calc(-50% + var(--x)),
                calc(-50% + var(--y))
              )
              scale(1.2)
              rotate(30deg);
            opacity: 0;
          }
        }
      `}</style>
      <div className="relative w-88 sm:w-[24rem] flex flex-col items-center gap-4 text-center overflow-x-visible">
        <div
          className="text-sm uppercase tracking-[0.3em] text-[#9BE7FF] z-100"
          style={{
            textShadow: generateTextShadow(0.55),
            animation: "gacha-float-in 420ms ease-out 120ms both",
          }}
        >
          {title}
        </div>
        <div
          className="text-3xl font-bold text-white z-100 capitalize"
          style={{
            animation: "gacha-float-in 420ms ease-out 160ms both",
          }}
        >
          <span style={{ textShadow: generateTextShadow(1.5) }}>
            {rewardText}
          </span>
        </div>
        <div className="relative flex flex-col items-center justify-center w-full h-40 overflow-x-visible">
          <div className="pointer-events-none absolute inset-0">
            {SPARKLE_BURSTS.map((sparkle, index) => (
              <div
                key={index}
                className={cn(
                  "absolute left-1/2 top-1/2 opacity-0",
                  sparkle.shape === "diamond" ? "rotate-45" : "",
                )}
                style={
                  {
                    animation: `gacha-sparkle 720ms ease-out ${0.2 + sparkle.delay}s forwards`,
                    "--x": `${sparkle.x}px`,
                    "--y": `${sparkle.y}px`,
                  } as unknown as CSSProperties
                }
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 480 480"
                  style={
                    {
                      width: `${sparkle.size}px`,
                      height: `${sparkle.size}px`,
                      color: "var(--color-yellow-200)",
                      filter: "drop-shadow(0 0 8px currentColor)",
                    } as CSSProperties
                  }
                >
                  <path
                    d="M480 240A240 240 0 0 1 240 0 240 240 0 0 1 0 240a240 240 0 0 1 240 240 240 240 0 0 1 240-240Z"
                    fill="currentColor"
                  ></path>
                </svg>
              </div>
            ))}
          </div>

          <div
            className="flex-1 flex items-center justify-center pointer-events-none"
            style={{
              animation:
                "gacha-pop 720ms cubic-bezier(0.16, 1, 0.3, 1) 0.1s both",
            }}
          >
            {shouldUseRewardIcons ? (
              <PrizeWrapper
                prizes={[
                  {
                    icon: REWARD_ICONS[1],
                    value: rewardValue,
                    isPositive: true,
                  },
                ]}
                iconSize="w-20"
                textSize="text-4xl"
              />
            ) : isCosmetic ? (
              <div className="relative flex flex-col items-center justify-center">
                <AvatarBorder
                  src={
                    userProfilePicture ||
                    "https://qpurukhqnrwjwytkcjre.supabase.co/storage/v1/object/public/profile/coach_monke.png"
                  }
                  decoration={alternatePrizeImageSrc}
                  size={100}
                />
                <span
                  className={cn(
                    `font-bold absolute -bottom-4 text-white text-4xl text-shadow-black text-nowrap`,
                  )}
                >
                  {alternatePrizeText}
                </span>
              </div>
            ) : (
              <div className="relative flex flex-col items-center justify-center">
                <img
                  src={alternatePrizeImageSrc}
                  className={`${alternatePrizeImageSrcSize}`}
                  alt={"Alternate Prize"}
                  style={{ imageRendering: "pixelated" }}
                />
                <span
                  className={cn(
                    `font-bold absolute -bottom-4 text-white text-4xl text-shadow-black text-nowrap`,
                  )}
                >
                  {alternatePrizeText}
                </span>
              </div>
            )}
          </div>
        </div>
        <div
          className="flex flex-col items-center gap-3 mt-6"
          style={{
            animation: "gacha-float-in 420ms ease-out 240ms both",
          }}
        >
          {description && (
            <div className="text-sm text-zinc-300 max-w-xs text-center">
              {description}
            </div>
          )}
          <button
            onClick={onClose}
            className="text-white/90 tracking-wider green-elevated-container text-sm sm:text-base uppercase py-2! px-6! mt-2! hover:cursor-pointer hover:brightness-95 hover:saturate-95 active:scale-98"
            style={{
              animation: "gacha-bob 1.1s ease-in-out 500ms infinite",
              textShadow: generateTextShadow(1),
            }}
          >
            {buttonText}
          </button>
        </div>
      </div>
    </div>
  );
}
